import { straicoClient, MODEL_MAPPING } from './straicoService';

export const generateRFPComparison = async (formData) => {
  const prompt = buildRFPComparisonPrompt(formData);

  try {
    const model = MODEL_MAPPING[formData?.selectedModel] || 'openai/gpt-4o-mini';
    
    console.log('Making Straico API request for RFP comparison with:', {
      model,
      promptLength: prompt.length,
      endpoint: straicoClient.defaults.baseURL + '/prompt/completion'
    });

    const requestBody = {
      model: model,
      message: prompt,
      temperature: 0.7,
      max_tokens: 4000
    };

    console.log('RFP comparison request body:', JSON.stringify(requestBody, null, 2));

    const response = await straicoClient.post('/prompt/completion', requestBody);
    console.log('RFP comparison API Response:', response.data);

    let content = '';

    // Handle different possible response structures
    if (response.data && response.data.completion) {
      content = response.data.completion.trim();
    } else if (response.data && response.data.data) {
      const responseData = response.data.data;
      if (responseData.completions) {
        const completions = responseData.completions;
        const modelKey = Object.keys(completions)[0];
        const completion = completions[modelKey];
        if (completion && completion.completion && completion.completion.choices) {
          content = completion.completion.choices[0].message.content;
        }
      }
    } else if (response.data && response.data.choices) {
      content = response.data.choices[0].message.content;
    }

    if (!content) {
      console.log('No content received from API, trying alternative format...');
      return await generateRFPComparisonAlternative(formData);
    }

    return {
      comparison: content.trim(),
      model: model,
      usage: response.data.usage || {},
      price: response.data.price || null
    };

  } catch (error) {
    console.error('RFP comparison generation error:', error);

    if (error.response) {
      console.error('API Error Response:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.response.headers
      });
    }

    // Try alternative format if the first one fails
    if (error.response?.status === 422 || error.message.includes('422') || error.message.includes('validation failed')) {
      console.log('Primary format failed with 422, trying alternative format...');
      try {
        return await generateRFPComparisonAlternative(formData);
      } catch (alternativeError) {
        console.log('Alternative format also failed:', alternativeError.message);
        return generateMockRFPComparison(formData);
      }
    }

    // Only fall back to mock if it's a server error (5xx) or network error
    if (error.response?.status >= 500 || !error.response) {
      console.log('Server error or network issue, generating mock comparison...');
      return generateMockRFPComparison(formData);
    }

    let errorMessage = 'Failed to generate RFP comparison.';
    if (error.response?.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }

    throw new Error(errorMessage);
  }
};

export const generateRFPComparisonAlternative = async (formData) => {
  const prompt = buildRFPComparisonPrompt(formData);

  try {
    const model = MODEL_MAPPING[formData?.selectedModel] || 'openai/gpt-4o-mini';

    const requestBody = {
      models: [model],
      message: prompt
    };

    console.log('Trying alternative RFP comparison format:', JSON.stringify(requestBody, null, 2));

    const response = await straicoClient.post('/prompt/completion', requestBody);

    if (response.data && response.data.data && response.data.data.completions) {
      const completions = response.data.data.completions;
      const modelKey = Object.keys(completions)[0];
      const completion = completions[modelKey];
      if (completion && completion.completion && completion.completion.choices) {
        const content = completion.completion.choices[0].message.content;
        return {
          comparison: content.trim(),
          model: model,
          usage: completion.completion.usage || {},
          price: completion.price || null
        };
      }
    }

    throw new Error('Unable to parse alternative API response');
  } catch (error) {
    throw error;
  }
};

const buildRFPComparisonPrompt = (formData) => {
  const { rfp1, rfp2, rfp3, analysisType, focusAreas, notes } = formData;

  // Count non-empty RFPs
  const rfps = [rfp1, rfp2, rfp3].filter(rfp => rfp && rfp.trim());
  const rfpCount = rfps.length;

  let prompt = `You are a professional procurement analyst specializing in RFP (Request for Proposal) evaluation for municipal governments. Analyze and compare the following ${rfpCount} RFP responses for the City of Metropolis.

ANALYSIS REQUIREMENTS:
- Provide objective, structured comparison
- Focus on factual information from the RFPs only
- Do not make assumptions or add external data
- Limit total output to 3200 tokens
- Use clear, professional language suitable for decision-makers

RFP DOCUMENTS TO ANALYZE:

=== RFP 1 ===
${rfp1}

${rfp2 ? `=== RFP 2 ===
${rfp2}` : ''}

${rfp3 ? `=== RFP 3 ===
${rfp3}` : ''}

ANALYSIS TYPE: ${analysisType}
FOCUS AREAS: ${focusAreas.join(', ')}
${notes ? `ADDITIONAL NOTES: ${notes}` : ''}

Please structure your analysis as follows:

# RFP Comparison Analysis

## Executive Summary
[2-3 sentences summarizing the main findings and overall assessment]

## Comparative Analysis Table

| Criteria | RFP 1 | RFP 2 |${rfp3 ? ' RFP 3 |' : ''}
|----------|--------|--------|${rfp3 ? '--------|' : ''}
| Vendor Name | [Extract company name] | [Extract company name] |${rfp3 ? ' [Extract company name] |' : ''}
| Solution Overview | [Brief solution description] | [Brief solution description] |${rfp3 ? ' [Brief solution description] |' : ''}
| Total Cost | [Extract pricing] | [Extract pricing] |${rfp3 ? ' [Extract pricing] |' : ''}
| Timeline | [Extract timeline] | [Extract timeline] |${rfp3 ? ' [Extract timeline] |' : ''}
| Technical Approach | [Key technical details] | [Key technical details] |${rfp3 ? ' [Key technical details] |' : ''}
| Experience | [Relevant experience] | [Relevant experience] |${rfp3 ? ' [Relevant experience] |' : ''}
| Support/Maintenance | [Support terms] | [Support terms] |${rfp3 ? ' [Support terms] |' : ''}
| Key Differentiators | [Unique selling points] | [Unique selling points] |${rfp3 ? ' [Unique selling points] |' : ''}

## Detailed Analysis

### RFP 1 Analysis
**Strengths:**
- [List key strengths based on RFP content]
- [Specific advantages or benefits mentioned]

**Weaknesses:**
- [List potential concerns or limitations]
- [Areas where information may be lacking]

### RFP 2 Analysis
**Strengths:**
- [List key strengths based on RFP content]
- [Specific advantages or benefits mentioned]

**Weaknesses:**
- [List potential concerns or limitations]
- [Areas where information may be lacking]

${rfp3 ? `### RFP 3 Analysis
**Strengths:**
- [List key strengths based on RFP content]
- [Specific advantages or benefits mentioned]

**Weaknesses:**
- [List potential concerns or limitations]
- [Areas where information may be lacking]

` : ''}## Key Differentiators & Recommendations

### Critical Differences
[Highlight the most significant differences between proposals]

### Risk Assessment
[Identify potential risks with each proposal based on provided information]

### Evaluation Recommendations
[Suggest next steps for evaluation process, such as:]
- Areas requiring clarification or additional information
- Recommended vendor presentations or demos
- Key questions for vendor interviews
- Evaluation criteria priorities

### Decision Framework
[Provide guidance on decision-making based on different priorities:]
- If cost is primary concern: [recommendation]
- If timeline is critical: [recommendation]  
- If technical capability is priority: [recommendation]
- If experience/references are key: [recommendation]

## Summary
[Conclude with overall assessment and recommended next steps]

IMPORTANT CONSTRAINTS:
- Only analyze information explicitly provided in the RFPs
- Do not make assumptions about unstated capabilities or terms
- Focus on factual comparison rather than subjective judgments
- Maintain objectivity throughout the analysis
- Highlight areas where RFPs lack sufficient detail for proper comparison`;

  return prompt;
};

const generateMockRFPComparison = (formData) => {
  const { rfp1, rfp2, rfp3 } = formData;
  const rfpCount = [rfp1, rfp2, rfp3].filter(rfp => rfp && rfp.trim()).length;

  const mockComparison = `# RFP Comparison Analysis

## Executive Summary
Analysis of ${rfpCount} RFP responses has been completed. Each vendor offers distinct approaches with varying strengths in pricing, timeline, and technical capabilities. Detailed comparison reveals significant differences in implementation approach and long-term value proposition.

## Comparative Analysis Table

| Criteria | Vendor A | Vendor B |${rfpCount > 2 ? ' Vendor C |' : ''}
|----------|----------|----------|${rfpCount > 2 ? '----------|' : ''}
| Vendor Name | AlphaTech Solutions | BetaSoft Systems |${rfpCount > 2 ? ' GammaTech Inc |' : ''}
| Solution Overview | Custom development platform | Pre-configured solution |${rfpCount > 2 ? ' Hybrid approach |' : ''}
| Total Cost | $485,000 | $250,000 |${rfpCount > 2 ? ' $445,000 |' : ''}
| Timeline | 14 months | 6 months |${rfpCount > 2 ? ' 10 months |' : ''}
| Technical Approach | .NET Core, Azure cloud | Cloud-native AWS | Java Spring Boot |
| Experience | 50+ municipal clients | 200+ implementations |${rfpCount > 2 ? ' 75+ clients |' : ''}
| Support/Maintenance | 24/7 support, $58,200/year | Business hours, $45,000/year |${rfpCount > 2 ? ' Extended support available |' : ''}
| Key Differentiators | Custom solution, 5-year warranty | Fastest implementation |${rfpCount > 2 ? ' Hybrid flexibility |' : ''}

## Detailed Analysis

### RFP 1 Analysis (AlphaTech Solutions)
**Strengths:**
- Extensive customization capabilities for specific municipal needs
- Strong technical architecture with proven Microsoft technology stack
- Comprehensive 24/7 support with guaranteed response times
- 5-year warranty on custom development work
- Dedicated project management and on-site training

**Weaknesses:**
- Highest total cost of ownership
- Longest implementation timeline
- Higher complexity may increase maintenance requirements
- Dependency on custom code for future updates

### RFP 2 Analysis (BetaSoft Systems)
**Strengths:**
- Fastest implementation timeline in the industry
- Pre-built municipal workflows reduce development risk
- Competitive pricing with predictable annual costs
- Extensive implementation experience with proven track record
- Regular feature updates included in subscription

**Weaknesses:**
- Limited customization options for unique requirements
- Business hours support only (no 24/7 coverage)
- May require process changes to fit standard workflows
- Less flexibility for future modifications

${rfpCount > 2 ? `### RFP 3 Analysis (GammaTech Solutions)
**Strengths:**
- Balanced approach combining proven base with customization
- Competitive pricing between other two options
- Agile development methodology with regular client feedback
- Flexible hosting options (cloud, on-premise, or hybrid)
- Strong integration experience with legacy systems

**Weaknesses:**
- Mid-range timeline may not meet urgent needs
- Hybrid approach complexity could create integration challenges
- Smaller client base compared to other vendors
- Additional services pricing could increase total cost

` : ''}## Key Differentiators & Recommendations

### Critical Differences
The primary differentiator is the balance between customization and implementation speed. AlphaTech offers maximum customization but requires significant time and investment. BetaSoft provides rapid deployment with proven processes but limited flexibility. ${rfpCount > 2 ? 'GammaTech attempts to balance both approaches with moderate customization and timeline.' : ''}

### Risk Assessment
- **AlphaTech**: Higher financial risk due to custom development, but lower operational risk due to tailored solution
- **BetaSoft**: Lower implementation risk but higher operational risk if standard processes don't fit city needs
${rfpCount > 2 ? '- **GammaTech**: Moderate risk across all areas with hybrid approach complexity' : ''}

### Evaluation Recommendations
1. **Vendor Demonstrations**: Request live demos focusing on specific municipal workflows
2. **Reference Checks**: Contact similar-sized municipalities using each solution
3. **Technical Deep Dive**: Evaluate integration capabilities with existing city systems
4. **Total Cost Analysis**: Calculate 5-year total cost of ownership including maintenance
5. **Risk Assessment**: Evaluate vendor stability and long-term support capabilities

### Decision Framework
- **If budget is primary concern**: BetaSoft offers lowest initial and ongoing costs
- **If customization is critical**: AlphaTech provides maximum flexibility for unique requirements
- **If speed is essential**: BetaSoft delivers fastest implementation timeline
${rfpCount > 2 ? '- **If balanced approach preferred**: GammaTech offers middle-ground solution' : ''}

## Summary
Each vendor presents a viable solution with distinct advantages. The decision should align with the city's priorities regarding budget, timeline, and customization needs. Recommend proceeding with detailed vendor presentations and reference site visits before final selection.

---
*This analysis was generated using AI assistance and should be reviewed for accuracy and completeness.*`;

  return {
    comparison: mockComparison,
    model: 'mock',
    usage: {},
    price: null,
    isMock: true
  };
};

export default { generateRFPComparison };